import { z } from "zod";
export const liraUnitSchema = z.enum(["COUNT", "PERCENTAGE", "RATIO", "RATE_PER_1000", "AGE_YEARS", "PEOPLE_PER_SQ_KM"]);
const finite = z.number().finite();
export const liraDatasetSchema = z.object({
    id: z.string().min(1), metric: z.string().min(1), title: z.string().min(1), unit: liraUnitSchema,
    scope: z.object({ label: z.string().min(1), barangayId: z.string().nullable() }).strict(),
    dimension: z.enum(["CATEGORY", "BARANGAY", "AGE_GROUP", "YEAR"]),
    calculation: z.enum(["COUNT", "ACTIVE_POP_PERCENTAGE", "CLASSIFIED_PERCENTAGE", "DEPENDENCY", "MEDIAN", "DENSITY", "PERIOD_GROWTH", "CAGR", "SNAPSHOT", "RATE_PER_1000"]),
    denominatorMetric: z.string().optional(),
    order: z.enum(["SOURCE", "VALUE_DESC", "VALUE_ASC", "AGE_ASC", "CHRONOLOGICAL"]),
    composition: z.object({ total: finite.nonnegative(), disjoint: z.boolean(), complete: z.boolean() }).strict().optional(),
    points: z.array(z.object({
        key: z.string().min(1), label: z.string().min(1), value: finite.nullable(),
        rawCount: finite.nonnegative().nullable().optional(), denominator: finite.nonnegative().nullable().optional(),
        time: finite.int().optional(), barangayId: z.string().optional(),
        details: z.record(z.string(), finite.nullable()).optional(),
    }).strict()),
}).strict();
export const liraUnitLabels = {
    COUNT: "count", PERCENTAGE: "%", RATIO: "per 100 working-age people", RATE_PER_1000: "per 1,000 females age 15–19", AGE_YEARS: "years", PEOPLE_PER_SQ_KM: "people/sq. km.",
};
export function formatLiraValue(value, unit = "COUNT") {
    if (value === null || value === undefined || !Number.isFinite(value))
        return "Unavailable";
    const number = value.toLocaleString("en-PH", { maximumFractionDigits: unit === "COUNT" ? 0 : 1 });
    return unit === "COUNT" ? number : unit === "PERCENTAGE" ? `${number}%` : `${number} ${liraUnitLabels[unit]}`;
}
function equal(a, b) { return Math.abs(a - b) <= Number.EPSILON * Math.max(1, Math.abs(a), Math.abs(b)) * 8; }
function unitLabel(dataset) { return dataset.calculation === "ACTIVE_POP_PERCENTAGE" ? "% of active population" : dataset.calculation === "CLASSIFIED_PERCENTAGE" ? "% of classified Male/Female records" : liraUnitLabels[dataset.unit]; }
export function validateLiraDataset(dataset, expectedBarangayId) {
    if (!liraDatasetSchema.safeParse(dataset).success)
        return "Malformed analytics or non-finite values; the chart is unavailable.";
    if (expectedBarangayId !== undefined && dataset.scope.barangayId !== expectedBarangayId)
        return "The analytics scope does not match the current Barangay context.";
    if (new Set(dataset.points.map((p) => p.key)).size !== dataset.points.length)
        return "Duplicate categories would misrepresent the result.";
    const units = { COUNT: "COUNT", SNAPSHOT: "COUNT", ACTIVE_POP_PERCENTAGE: "PERCENTAGE", CLASSIFIED_PERCENTAGE: "PERCENTAGE", DEPENDENCY: "RATIO", MEDIAN: "AGE_YEARS", DENSITY: "PEOPLE_PER_SQ_KM", PERIOD_GROWTH: "PERCENTAGE", CAGR: "PERCENTAGE", RATE_PER_1000: "RATE_PER_1000" };
    if (dataset.unit !== units[dataset.calculation])
        return "The calculation and unit are incompatible.";
    for (const p of dataset.points) {
        if (dataset.scope.barangayId && p.barangayId && p.barangayId !== dataset.scope.barangayId)
            return "A category is outside the analytics scope.";
        if (dataset.unit === "COUNT" && p.value !== null && (!Number.isSafeInteger(p.value) || p.value < 0))
            return "Counts must be nonnegative whole numbers.";
        if (p.rawCount !== undefined && p.rawCount !== null && !Number.isSafeInteger(p.rawCount))
            return "The original count is invalid.";
        if (dataset.unit === "COUNT" && p.rawCount !== undefined && p.value !== p.rawCount)
            return "The count does not match its original deterministic value.";
        if (p.denominator !== undefined && p.denominator !== null && (!Number.isSafeInteger(p.denominator) || p.rawCount !== undefined && p.rawCount !== null && p.rawCount > p.denominator))
            return "The original count and population denominator are inconsistent.";
        if (dataset.calculation === "ACTIVE_POP_PERCENTAGE" || dataset.calculation === "CLASSIFIED_PERCENTAGE") {
            const validDenominator = p.denominator !== undefined && p.denominator !== null && p.denominator > 0;
            if (p.value === null && validDenominator && p.rawCount !== undefined && p.rawCount !== null)
                return "A calculable percentage was incorrectly marked unavailable.";
            if (p.value !== null && (!validDenominator || p.rawCount === undefined || p.rawCount === null || p.rawCount > p.denominator || !equal(p.value, p.rawCount / p.denominator * 100)))
                return "The percentage does not match its original count and stated denominator.";
        }
    }
    return null;
}
/** Analytical conversion, shared by server and cached-result consumers. This is
 * deliberately separate from presentation: original counts are never recovered
 * by rounding a displayed percentage or by summing chart slices. */
export function convertLiraMeasure(dataset, measure) {
    if (validateLiraDataset(dataset))
        return null;
    if (measure === "COUNT" && dataset.unit === "COUNT")
        return dataset;
    if (measure === "PERCENTAGE" && dataset.unit === "PERCENTAGE" && ["ACTIVE_POP_PERCENTAGE", "CLASSIFIED_PERCENTAGE"].includes(dataset.calculation))
        return dataset;
    if (measure === "PERCENTAGE" && dataset.unit !== "COUNT" || measure === "COUNT" && !["ACTIVE_POP_PERCENTAGE", "CLASSIFIED_PERCENTAGE"].includes(dataset.calculation))
        return null;
    if (!dataset.denominatorMetric || dataset.points.some((p) => p.rawCount === undefined || p.denominator === undefined))
        return null;
    return { ...dataset, unit: measure === "COUNT" ? "COUNT" : "PERCENTAGE", calculation: measure === "COUNT" ? "COUNT" : dataset.denominatorMetric.startsWith("CLASSIFIED") ? "CLASSIFIED_PERCENTAGE" : "ACTIVE_POP_PERCENTAGE",
        points: dataset.points.map((p) => ({ ...p, value: measure === "COUNT" ? p.rawCount : p.rawCount !== null && p.denominator !== null && p.denominator > 0 ? p.rawCount / p.denominator * 100 : null })),
    };
}
function ordered(dataset) {
    const points = [...dataset.points];
    if (dataset.order === "CHRONOLOGICAL")
        points.sort((a, b) => (a.time ?? Infinity) - (b.time ?? Infinity));
    if (dataset.order === "AGE_ASC")
        points.sort((a, b) => Number(a.label.match(/^\d+/)?.[0] ?? Infinity) - Number(b.label.match(/^\d+/)?.[0] ?? Infinity));
    if (dataset.order === "VALUE_DESC")
        points.sort((a, b) => (b.value ?? -Infinity) - (a.value ?? -Infinity));
    if (dataset.order === "VALUE_ASC")
        points.sort((a, b) => (a.value ?? Infinity) - (b.value ?? Infinity));
    return { ...dataset, points };
}
export function liraDatasetChartProblem(dataset, type) {
    const invalid = validateLiraDataset(dataset);
    if (invalid)
        return invalid;
    if (!dataset.points.length || dataset.points.every((p) => p.value === null))
        return "No calculable values exist for this chart; see the unavailable entries in the table.";
    if (type === "LINE" && (dataset.dimension !== "YEAR" || dataset.points.length < 2 || dataset.points.some((p) => p.time === undefined) || new Set(dataset.points.map((p) => p.time)).size !== dataset.points.length))
        return "A line graph requires genuine ordered historical/time-series data. A categorical bar chart/table is shown instead.";
    if (type === "DONUT") {
        const c = dataset.composition;
        const sum = dataset.points.reduce((n, p) => n + (p.rawCount ?? p.value ?? 0), 0);
        if (!c?.disjoint || !c.complete || c.total <= 0 || !equal(sum, c.total) || dataset.points.some((p) => p.value === null || p.value < 0 || dataset.unit === "PERCENTAGE" && p.denominator !== c.total) || !["COUNT", "PERCENTAGE"].includes(dataset.unit))
            return "These categories are not complete, non-overlapping parts of one whole. A bar chart/table is shown instead of a donut.";
    }
    return null;
}
export function liraAnalyticsTable(datasets) {
    const hasCounts = datasets.some((d) => d.unit === "PERCENTAGE" && d.points.some((p) => p.rawCount !== undefined));
    const detailKeys = [...new Set(datasets.flatMap((d) => d.points.flatMap((p) => Object.keys(p.details ?? {}))))];
    return {
        title: datasets.length === 1 ? `${datasets[0]?.title} — ${datasets[0]?.scope.label}` : `RBIM comparison — ${datasets[0]?.scope.label ?? "authorized scope"}`,
        columns: [{ key: "scope", label: "Category / Barangay", format: "TEXT" }, { key: "metric", label: "Metric", format: "TEXT" }, { key: "value", label: "Value", format: "NUMBER", unitKey: "unitCode" }, { key: "unit", label: "Unit", format: "TEXT" },
            ...(hasCounts ? [{ key: "rawCount", label: "Original count", format: "NUMBER", unit: "COUNT" }, { key: "denominator", label: "Denominator", format: "NUMBER", unit: "COUNT" }] : []),
            ...detailKeys.map((key) => ({ key, label: key, format: "NUMBER", unit: key === "young" || key === "old" ? "RATIO" : "COUNT" }))],
        rows: datasets.flatMap((d) => d.points.map((p) => ({ scope: p.label, metric: d.metric, value: p.value ?? "Unavailable", unit: unitLabel(d), unitCode: d.unit,
            ...(hasCounts ? { rawCount: p.rawCount ?? "Unavailable", denominator: p.denominator ?? "Unavailable" } : {}),
            ...Object.fromEntries(detailKeys.map((key) => [key, p.details?.[key] ?? "Unavailable"])),
        }))),
    };
}
/** Presentation uses the canonical points, never the first arbitrary numeric
 * table column or model-generated values. One dataset has exactly one unit. */
export function presentLiraAnalytics(analytics, format) {
    if (!analytics.datasets)
        return { analytics: { ...analytics, visualizations: [] }, warning: "This older report lacks verified metric/unit metadata. Its exact table is available; request the metric again for a validated chart." };
    const warnings = [];
    const datasets = analytics.datasets.filter((d) => {
        const invalid = validateLiraDataset(d, analytics.scope?.barangayId);
        if (invalid)
            warnings.push(invalid);
        return !invalid;
    }).map(ordered);
    const visualizations = [];
    for (const d of datasets) {
        const invalid = validateLiraDataset(d, analytics.scope?.barangayId);
        if (invalid) {
            warnings.push(invalid);
            continue;
        }
        if (format === "TABLE")
            continue;
        let type = format === "DONUT" ? "DONUT" : format === "LINE" ? "LINE" : d.dimension === "YEAR" && format === "AUTO" ? "LINE" : "BAR";
        const problem = liraDatasetChartProblem(d, type);
        if (problem) {
            warnings.push(problem);
            type = "BAR";
        }
        if (liraDatasetChartProblem(d, "BAR"))
            continue;
        visualizations.push({ id: d.id, datasetId: d.id, type, title: `${d.title} — ${d.scope.label}`, seriesLabel: unitLabel(d), unit: d.unit,
            data: d.points.map((p) => ({ label: p.label, value: p.value, ...(p.time !== undefined ? { time: p.time } : {}) })) });
    }
    const safeDatasets = datasets.filter((d) => !validateLiraDataset(d, analytics.scope?.barangayId));
    return { analytics: { ...analytics, datasets, warnings: [...new Set(warnings)], displayFormat: format, table: liraAnalyticsTable(safeDatasets), visualizations }, ...(warnings.length ? { warning: [...new Set(warnings)].join(" ") } : {}) };
}
export function createLiraAnalytics(scope, datasets, format = "AUTO", generatedAt = new Date().toISOString()) {
    return presentLiraAnalytics({ scope, datasets, generatedAt, source: "AUTHORIZED_RBIM_RECORDS", sourceLabel: scope.label, table: null, visualizations: [] }, format);
}
/** Final boundary check also catches tampering/mismatches in rendered series. */
export function validateLiraAnalytics(analytics, expectedBarangayId) {
    if (!analytics.scope || expectedBarangayId !== undefined && analytics.scope.barangayId !== expectedBarangayId)
        return "The analytics scope does not match the current Barangay context.";
    if (!z.array(liraDatasetSchema).safeParse(analytics.datasets).success || analytics.datasets.some((item) => validateLiraDataset(item, analytics.scope.barangayId) || item.scope.label !== analytics.scope.label))
        return "Malformed analytics or an inconsistent geographic scope; the report is unavailable.";
    if (new Set(analytics.datasets.map((d) => d.id)).size !== analytics.datasets.length)
        return "Duplicate analytical series would misrepresent the report.";
    const expectedTable = liraAnalyticsTable(analytics.datasets);
    if (!analytics.table || JSON.stringify(analytics.table.rows) !== JSON.stringify(expectedTable.rows) || JSON.stringify(analytics.table.columns) !== JSON.stringify(expectedTable.columns))
        return "The table does not match the same verified metric values and units as the chart.";
    return null;
}
export function validateLiraVisualization(analytics, visualization, expectedBarangayId) {
    const reportProblem = validateLiraAnalytics(analytics, expectedBarangayId);
    if (reportProblem)
        return reportProblem;
    const d = analytics.datasets?.find((item) => item.id === visualization.datasetId);
    if (!d)
        return "No canonical dataset is bound to this chart; it cannot be rendered accurately.";
    const invalid = validateLiraDataset(d, expectedBarangayId);
    if (invalid)
        return invalid;
    if (!["BAR", "DONUT", "LINE"].includes(visualization.type) || !Array.isArray(visualization.data) || d.unit !== visualization.unit || visualization.data.length !== d.points.length || visualization.data.some((p, i) => { const expected = d.points[i]; return !p || !expected || p.label !== expected.label || p.value !== expected.value || p.time !== expected.time; }))
        return "The chart values or units do not match the verified analytical result.";
    const problem = liraDatasetChartProblem(d, visualization.type);
    if (problem)
        return problem;
    if (visualization.type === "LINE" && d.points.some((p, i) => i > 0 && p.time <= d.points[i - 1].time))
        return "Historical chart values must be chronological.";
    return null;
}
//# sourceMappingURL=lira-analytics.js.map